/** Visual-only combat feedback. Never affects GameSession outcomes. */

export const COMBAT_FX_CAP = 64

export type CombatFx =
  | {
      type: 'splash'
      x: number
      y: number
      radius: number
      color?: number
      /** Inner shockwave ring (Cannon LV4). */
      innerRadius?: number
    }
  | {
      type: 'damageNumber'
      x: number
      y: number
      amount: number
      /** Presentation tier cue — amount is still rounded dealt (not rescaled). */
      style: 'direct' | 'splash' | 'dot' | 'shield' | 'amplified'
    }
  | {
      type: 'goldPopup'
      x: number
      y: number
      amount: number
    }
  | {
      type: 'leak'
      x: number
      y: number
      amount: number
    }
  | {
      type: 'hitFlash'
      enemyId: string
      shield: boolean
    }
  | {
      type: 'death'
      x: number
      y: number
      color?: number
      /** Boss / air-boss — slightly heavier burst (not a cutscene). */
      heavy?: boolean
    }
  | {
      type: 'towerPulse'
      towerId: string
      kind: 'fire' | 'upgrade' | 'sell'
      /** AA LV4 multi-lock volley — presentation cue only. */
      multiLock?: boolean
    }
  | {
      type: 'contagion'
      fromX: number
      fromY: number
      toX: number
      toY: number
    }
  | {
      type: 'deepFreezeFlash'
      x: number
      y: number
    }
  | {
      type: 'waveClear'
      reward: number
    }
  | {
      /** Shield reached zero — presentation only. */
      type: 'shieldBreak'
      x: number
      y: number
      enemyId: string
    }
  | {
      /** Actual regen HP gain this slice — not delay countdown. */
      type: 'regenHeal'
      x: number
      y: number
      enemyId: string
      amount: number
    }
  | {
      /** Burn status applied (FIRE LV4). */
      type: 'burnApply'
      x: number
      y: number
      enemyId: string
    }
  | {
      /** Armor Break applied (Mage). fracture = LV4 stronger magnitude. */
      type: 'armorBreakApply'
      x: number
      y: number
      enemyId: string
      fracture?: boolean
    }
  | {
      /** Throttled resist/immune cue — not per DoT tick. */
      type: 'statusReject'
      x: number
      y: number
      enemyId: string
      kind: 'IMMUNE' | 'RESISTED'
      statusId: string
    }
  | {
      /** Projectile impact cue (non-splash families). */
      type: 'impact'
      x: number
      y: number
      family: 'fire' | 'frost' | 'cannon' | 'poison' | 'aa' | 'mage'
      /** True when LV4 Burn impact vs ordinary. */
      signature?: boolean
    }

/** Drop priority when queue is full — lower score drops first. */
function dropScore(fx: CombatFx): number {
  switch (fx.type) {
    case 'waveClear':
    case 'leak':
    case 'death':
    case 'shieldBreak':
    case 'deepFreezeFlash':
    case 'contagion':
      return 100
    case 'burnApply':
    case 'armorBreakApply':
    case 'regenHeal':
    case 'statusReject':
      return 70
    case 'splash':
      return fx.innerRadius !== undefined ? 80 : 40
    case 'towerPulse':
      return fx.kind === 'fire' ? 35 : 90
    case 'impact':
      return fx.signature ? 55 : 30
    case 'hitFlash':
      return 25
    case 'damageNumber':
      return fx.style === 'dot' ? 10 : 20
    case 'goldPopup':
      return 15
    default:
      return 5
  }
}

export function pushCombatFx(queue: CombatFx[], fx: CombatFx, cap = COMBAT_FX_CAP): void {
  if (queue.length >= cap) {
    let dropIndex = 0
    let lowest = dropScore(queue[0]!)
    for (let i = 1; i < queue.length; i += 1) {
      const score = dropScore(queue[i]!)
      if (score < lowest) {
        lowest = score
        dropIndex = i
      }
    }
    // Never drop essential cues in favor of floaters if the new event is also low.
    if (dropScore(fx) <= lowest && lowest >= 70) {
      return
    }
    queue.splice(dropIndex, 1)
  }
  queue.push(fx)
}
